import { Knob } from "#web/components/common/Knob/Knob";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { i18nLike } from "#web/services/i18nLike";
import { Badge, Banner, BlockStack, Card, Icon, InlineStack, Text, Tooltip } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { CardSkeleton } from "../common/CardSkeleton";

const en = {
  asOf: "as of {{date}}",
  avgCommission: "avg. commission +",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "There was an error loading the recovered revenue with commission metric card",
  },
  includeCommission: "Include commission",
  insufficientData: "Insufficient data. May take a few hours/days to appear",
  lifetimeRoi: "Lifetime ROI was introduced with an update on {{date}}. Lifetime ROI is tracked after this update.",
  ordersTimes: "orders *",
  recoveredRevenue: "Recovered revenue",
  recoveredRevenueCommission: "Recovered revenue commission",
  roiLifetime: "ROI Lifetime",
};

interface RecoveredRevenueWithCommissionMetricCardProps {
  currency: string;
  orderCount: string | undefined;
  recoveredRevenue: string | undefined;
  recoveredRevenueCommission: string | undefined;
}

export const RecoveredRevenueWithCommissionMetricCard = ({
  currency,
  orderCount,
  recoveredRevenue,
  recoveredRevenueCommission,
}: RecoveredRevenueWithCommissionMetricCardProps) => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [includeCommission, setIncludeCommission] = useState<boolean>(true);

  if (shopError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  if (shopFetching) {
    return <CardSkeleton />;
  }

  return (
    <Card>
      <InlineStack align="space-between" gap="100">
        <BlockStack gap="200">
          <InlineStack align="start" blockAlign="center" gap="150">
            <Text as="h2" variant="headingMd">
              {t("roiLifetime")}
            </Text>
            {shop && new Date(shop.createdAt) <= new Date("2025-03-15") && (
              <Tooltip
                content={
                  <Text as="span" tone="subdued" variant="bodySm">
                    {t("lifetimeRoi", { date: "15.03.2025" })}
                  </Text>
                }
                width="wide"
              >
                <div style={{ textDecoration: "underline dotted" }}>
                  <Text as="span" variant="bodyXs">
                    {t("asOf", { date: "15.03.2025" })}
                  </Text>
                </div>
              </Tooltip>
            )}
          </InlineStack>
          {recoveredRevenueCommission ? (
            <Text as="span" variant="headingLg">
              {includeCommission
                ? (Number(recoveredRevenue) + Number(recoveredRevenueCommission)).toFixed(2)
                : Number(recoveredRevenue)}
              <Text as="span" variant="bodyMd">
                {" "}
                {currency}
              </Text>
            </Text>
          ) : (
            <Badge>{t("insufficientData")}</Badge>
          )}
        </BlockStack>
        {recoveredRevenueCommission && (
          <BlockStack gap="200">
            <InlineStack align="start" blockAlign="center" gap="200">
              <Tooltip
                content={
                  <>
                    <InlineStack align="center" gap="100">
                      <Badge size="small">{orderCount}</Badge>
                      <Text as="span" variant="bodySm">
                        {t("ordersTimes")}
                      </Text>
                      <Badge size="small">15%</Badge>
                      <Text as="span" variant="bodySm">
                        {t("avgCommission")}
                      </Text>

                      <Badge size="small">{`${recoveredRevenue ?? ""} ${currency}`}</Badge>
                      <Text as="span" variant="bodySm">
                        {t("recoveredRevenue")}
                      </Text>
                    </InlineStack>
                  </>
                }
                width="wide"
              >
                <InlineStack align="start" blockAlign="center" gap="0">
                  <Icon source={InfoIcon} tone="subdued" />
                  <Text as="span" tone="subdued" variant="bodyXs">
                    {t("includeCommission")}
                  </Text>
                </InlineStack>
              </Tooltip>
              <Knob
                ariaLabel={t("includeCommission")}
                onClick={() => {
                  setIncludeCommission(!includeCommission);
                }}
                selected={includeCommission}
              />
            </InlineStack>
          </BlockStack>
        )}
      </InlineStack>
    </Card>
  );
};
